import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import { useTheme } from "@/app/providers/ThemeContext";
import { FONT_MONO, FONT_SERIF } from "@/shared/lib/typography";

gsap.registerPlugin(ScrollTrigger);

const tabs = [
    { label: "All Questions", value: "all" },
    { label: "Plans & Pricing", value: "plans" },
    { label: "Features", value: "features" },
    { label: "Alerts", value: "alerts" },
];

const faqs = [
    {
        cat: "plans",
        q: "What's included in the free trial?",
        a: "The 14-day free trial includes all LinkedHash capabilities: full access to options intelligence, volatility analysis, historical context, and the Oracle model. You get 2 active watches, 5-day max duration, 15-minute evaluation intervals, and 3 alerts per watch. No credit card required.",
    },
    {
        cat: "plans",
        q: "What's the difference between Toolbox and Oracle?",
        a: "Toolbox ($18/mo) gives you access to all LinkedHash tools inside Claude - run any analysis or playbook on demand, but with no automated watches or alerts. Oracle ($29/mo) adds automation: 10 active watches, 5-minute evaluation intervals, 20 alerts per watch, and email support.",
        defaultOpen: true,
    },
    {
        cat: "plans",
        q: "Can I upgrade or downgrade my plan?",
        a: "Yes. You can upgrade from Toolbox to Oracle or Oracle Monitor at any time. Downgrading from Oracle will deactivate automated watches while your Toolbox plan remains active as long as your Toolbox subscription is active.",
    },
    {
        cat: "features",
        q: "What is the Oracle model?",
        a: "The Premier Oracle Model synthesizes option chain data, volatility surface, unusual flow, and historical context across all LinkedHash tools to deliver a single clear, explainable recommendation — should I buy or sell this contract right now? It's the highest level of analysis LinkedHash offers.",
    },
    {
        cat: "features",
        q: "What data does LinkedHash ingest?",
        a: "LinkedHash continuously ingests options chains (calls, puts, strike, expiry, premium), Greeks (Delta, Gamma, Vega, Theta), implied volatility and skew, options flow, volume and open interest, plus fundamentals including CPI, FOMC events, P/E, EPS, Revenue, and Guidance.",
    },
    {
        cat: "alerts",
        q: "What alert channels are supported?",
        a: "LinkedHash supports Slack, Telegram, Email, and Webhooks for alert delivery. Set up your preferred channel once and receive triggered alerts automatically whenever your watch conditions are met.",
    },
    {
        cat: "alerts",
        q: "How often does LinkedHash evaluate my watches?",
        a: "Oracle and Oracle Monitor plans evaluate watch conditions every 5 minutes during market hours. The free trial evaluates every 15 minutes. Oracle Monitor also supports continuous monitoring even when you're offline, ensuring no triggered conditions are missed.",
    },
    {
        cat: "features",
        q: "Does LinkedHash work with Claude Desktop?",
        a: "Yes. LinkedHash is designed to run as an MCP (Model Context Protocol) server alongside Claude. It integrates directly into Claude Desktop, Cursor, and any MCP-compatible host, so your tools appear exactly where you already work.",
    },
];

function FaqItem({ faq, isOpen, onToggle, animRef }) {
    const { theme } = useTheme();
    const isDark = theme === "dark";
    const answerRef = useRef(null);

    useEffect(() => {
        const el = answerRef.current;
        if (!el) return;
        if (isOpen) {
            gsap.fromTo(el,
                { height: 0, opacity: 0 },
                { height: "auto", opacity: 1, duration: 0.32, ease: "power2.out" }
            );
        } else {
            gsap.to(el, { height: 0, opacity: 0, duration: 0.22, ease: "power2.in" });
        }
    }, [isOpen]);

    return (
        <div
            ref={animRef}
            style={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e5e5e5" }}
        >
            <button
                onClick={onToggle}
                aria-expanded={isOpen}
                style={{
                    width: "100%", display: "flex", alignItems: "center",
                    justifyContent: "space-between", padding: "16px 0",
                    textAlign: "left", background: "none", border: "none",
                    cursor: "pointer",
                    fontFamily: FONT_SERIF, fontSize: "0.92rem",
                    color: isOpen ? "var(--lh-cyan, #00b8db)" : (isDark ? "var(--lh-text, #c8d8e8)" : "#1a1a1a"),
                    lineHeight: 1.4, gap: "14px", transition: "color 0.2s",
                }}
            >
                {faq.q}
                <span style={{
                    width: "22px", height: "22px", borderRadius: "50%", flexShrink: 0,
                    border: `1px solid ${isOpen ? "var(--lh-cyan, #00b8db)" : (isDark ? "rgba(255,255,255,0.15)" : "#e5e5e5")}`,
                    display: "grid", placeItems: "center",
                    fontSize: "0.85rem", color: isOpen ? (isDark ? "#000000" : "white") : (isDark ? "var(--lh-text-dim, #7f93a8)" : "#999"),
                    background: isOpen ? "var(--lh-cyan, #00b8db)" : "transparent",
                    transform: isOpen ? "rotate(45deg)" : "none",
                    transition: "all 0.2s",
                }}>
                    +
                </span>
            </button>
            <div
                ref={answerRef}
                aria-hidden={!isOpen}
                style={{
                    height: isOpen ? "auto" : 0,
                    overflow: "hidden",
                    opacity: isOpen ? 1 : 0,
                }}
            >
                <div style={{
                    fontSize: "0.8rem", color: isDark ? "var(--lh-text-dim)" : "#666", lineHeight: 1.75,
                    fontStyle: "italic", paddingBottom: "14px",
                }}>
                    {faq.a}
                </div>
            </div>
        </div>
    );
}

export default function FAQ() {
    const { theme } = useTheme();
    const isDark = theme === "dark";
    const sectionRef = useRef(null);
    const leftRef = useRef(null);
    const rightRef = useRef(null);
    const badgeRef = useRef(null);
    const h2Ref = useRef(null);
    const cboxRef = useRef(null);
    const tabsRef = useRef(null);
    const itemRefs = useRef([]);

    const [activeTab, setActiveTab] = useState("all");
    const [openIndex, setOpenIndex] = useState(
        faqs.findIndex((f) => f.defaultOpen)
    );

    const visible = faqs.filter((f) => activeTab === "all" || f.cat === activeTab);

    useEffect(() => {
        const ctx = gsap.context(() => {

            // ── Left column: badge → h2 → cbox ───────────────────────────────
            gsap.timeline({
                scrollTrigger: { trigger: leftRef.current, start: "top 82%", toggleActions: "play none none none" },
            })
                .fromTo(badgeRef.current,
                    { opacity: 0, y: 16, scale: 0.93 },
                    { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: "power3.out" }
                )
                .fromTo(h2Ref.current,
                    { opacity: 0, y: 22 },
                    { opacity: 1, y: 0, duration: 0.55, ease: "power3.out" },
                    "-=0.25"
                )
                .fromTo(cboxRef.current,
                    { opacity: 0, y: 18 },
                    { opacity: 1, y: 0, duration: 0.45, ease: "power2.out" },
                    "-=0.2"
                );

            // ── Right column: tabs then items ─────────────────────────────────
            gsap.timeline({
                scrollTrigger: { trigger: rightRef.current, start: "top 82%", toggleActions: "play none none none" },
            })
                .fromTo(tabsRef.current,
                    { opacity: 0, y: 14 },
                    { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" }
                )
                .fromTo(
                    itemRefs.current.filter(Boolean),
                    { opacity: 0, y: 20 },
                    { opacity: 1, y: 0, duration: 0.4, stagger: 0.07, ease: "power2.out" },
                    "-=0.15"
                );

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    // Re-animate items when tab changes
    const handleTabChange = (val) => {
        setActiveTab(val);
        // Brief stagger re-entrance after filter
        setTimeout(() => {
            gsap.fromTo(
                itemRefs.current.filter(Boolean),
                { opacity: 0, y: 12 },
                { opacity: 1, y: 0, duration: 0.3, stagger: 0.06, ease: "power2.out" }
            );
        }, 10);
    };

    return (
        <div id="faq" ref={sectionRef} style={{ background: isDark ? "var(--lh-bg)" : "#f7f7f5" }}>
            <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "72px 24px" }}>
                {/* ── Main Layout ─────────────────────────────────────────────── */}
                <div
                    className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8 md:gap-[80px]"
                >

                    {/* ── Left ─────────────────────────────────────────────────── */}
                    <div ref={leftRef}>
                        <div
                            ref={badgeRef}
                            className="inline-flex items-center gap-2 mb-4"
                            style={{
                                fontFamily: FONT_MONO, fontSize: "0.6rem",
                                letterSpacing: "0.12em", textTransform: "uppercase",
                                color: isDark ? "var(--lh-text-dim)" : "rgba(0,0,0,0.4)", border: isDark ? "1px solid var(--lh-cyan-glow)" : "1px solid rgba(0,0,0,0.12)",
                                borderRadius: "100px", padding: "6px 14px", opacity: 0,
                            }}
                        >
                            <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: isDark ? "var(--lh-cyan)" : "#00B8DB", display: "inline-block" }} />
                            FAQs
                        </div>

                        <h2
                            ref={h2Ref}
                            style={{
                                fontFamily: FONT_SERIF, fontSize: "2.5rem",
                                fontWeight: 700, lineHeight: 1.1, color: isDark ? "var(--lh-cyan)" : "#1a1a1a",
                                letterSpacing: "-0.02em", opacity: 0,
                            }}
                        >
                            FAQ
                        </h2>

                        <div
                            ref={cboxRef}
                            style={{
                                background: isDark ? "var(--lh-bg-2)" : "#ffffff", borderRadius: "16px", padding: "22px",
                                border: isDark ? "1px solid var(--lh-cyan-glow)" : "1px solid #e5e5e5", marginTop: "28px", opacity: 0,
                            }}
                        >
                            <div style={{ fontSize: "0.8rem", color: isDark ? "var(--lh-text-dim)" : "#666", lineHeight: 1.6, fontStyle: "italic", marginBottom: "12px" }}>
                                Have more questions? Contact our team — we're here to help.
                            </div>
                            <a
                                href="#"
                                style={{
                                    display: "inline-flex", alignItems: "center", gap: "7px",
                                    fontFamily: FONT_MONO, fontSize: "0.65rem",
                                    letterSpacing: "0.05em", padding: "12px 20px", borderRadius: "100px",
                                    minHeight: "44px",
                                    textDecoration: "none", background: "var(--lh-cyan)", color: "#000000",
                                    border: "1px solid var(--lh-cyan)",
                                    transition: "background 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = "#19d8ff";
                                    e.currentTarget.style.borderColor = "#19d8ff";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = "var(--lh-cyan)";
                                    e.currentTarget.style.borderColor = "var(--lh-cyan)";
                                }}
                            >
                                Contact Us →
                            </a>
                        </div>
                    </div>

                    {/* ── Right ────────────────────────────────────────────────── */}
                    <div ref={rightRef}>
                        {/* Tabs */}
                        <div ref={tabsRef} className="flex flex-wrap gap-2" style={{ marginBottom: "24px", opacity: 0 }}>
                            {tabs.map((tab) => (
                                <button
                                    key={tab.value}
                                    onClick={() => handleTabChange(tab.value)}
                                    style={{
                                        fontFamily: FONT_MONO, fontSize: "0.65rem",
                                        padding: "10px 16px", borderRadius: "100px", border: "none",
                                        cursor: "pointer", letterSpacing: "0.04em", transition: "all 0.2s",
                                        minHeight: "44px",
                                        background: activeTab === tab.value ? "var(--lh-cyan, #00b8db)" : (isDark ? "var(--lh-bg-3, #162130)" : "#ebebeb"),
                                        color: activeTab === tab.value ? "#000000" : (isDark ? "var(--lh-text, #c8d8e8)" : "#666"),
                                    }}
                                    onMouseEnter={(e) => {
                                        if (activeTab !== tab.value) {
                                            e.currentTarget.style.background = isDark ? "#1c2938" : "#e0e0e0";
                                            e.currentTarget.style.color = isDark ? "var(--lh-text, #c8d8e8)" : "#1a1a1a";
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (activeTab !== tab.value) {
                                            e.currentTarget.style.background = isDark ? "var(--lh-bg-3, #162130)" : "#ebebeb";
                                            e.currentTarget.style.color = isDark ? "var(--lh-text, #c8d8e8)" : "#666";
                                        }
                                    }}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* FAQ items */}
                        <div>
                            {visible.map((faq, i) => (
                                <FaqItem
                                    key={faq.q}
                                    faq={faq}
                                    isOpen={openIndex === faqs.indexOf(faq)}
                                    onToggle={() => setOpenIndex(
                                        openIndex === faqs.indexOf(faq) ? -1 : faqs.indexOf(faq)
                                    )}
                                    animRef={(el) => { itemRefs.current[i] = el; }}
                                />
                            ))}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}

